import { editor as monacoEditor } from 'monaco-editor';

/**
 * Decorator for highlighting table names in SQL
 */
export class TableNameHighlighter {
  private decorations: string[] = [];

  private isUpdating: boolean = false;

  private decorationType: monacoEditor.IModelDecorationOptions;

  constructor() {
    // Create decoration type for table names
    this.decorationType = {
      inlineClassName: 'sql-table-name',
      stickiness:
        monacoEditor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
    };
  }

  /**
   * Update decorations based on the current text
   */
  updateDecorations(editor: monacoEditor.IStandaloneCodeEditor) {
    // Prevent recursive calls
    if (this.isUpdating) return;

    const model = editor.getModel();
    if (!model) return;

    this.isUpdating = true;
    try {
      const text = model.getValue();
      const newDecorations: monacoEditor.IModelDeltaDecoration[] = [];

      // Pattern to match table names in various contexts
      const patterns = [
        // FROM table_name or FROM schema.table_name
        /(?:FROM|JOIN|INTO|UPDATE|TABLE)\s+(?:`[^`]+`|(\w+\.)?(\w+))/gi,
        // Qualified identifiers that look like schema.table
        /\b(\w+)\.(\w+)(?=\s|,|$)/g,
      ];

      for (const pattern of patterns) {
        let match;
        while ((match = pattern.exec(text)) !== null) {
          const startPos = model.getPositionAt(match.index);
          const endPos = model.getPositionAt(match.index + match[0].length);

          // Skip the keyword part
          const keywordMatch = match[0].match(
            /^(FROM|JOIN|INTO|UPDATE|TABLE)\s+/i,
          );
          if (keywordMatch) {
            const keywordLength = keywordMatch[0].length;
            const tableStartPos = model.getPositionAt(
              match.index + keywordLength,
            );

            newDecorations.push({
              range: {
                startLineNumber: tableStartPos.lineNumber,
                startColumn: tableStartPos.column,
                endLineNumber: endPos.lineNumber,
                endColumn: endPos.column,
              },
              options: this.decorationType,
            });
          } else {
            // For qualified identifiers, highlight the whole thing
            newDecorations.push({
              range: {
                startLineNumber: startPos.lineNumber,
                startColumn: startPos.column,
                endLineNumber: endPos.lineNumber,
                endColumn: endPos.column,
              },
              options: this.decorationType,
            });
          }
        }
      }

      // Apply decorations
      this.decorations = editor.deltaDecorations(
        this.decorations,
        newDecorations,
      );
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Clear all decorations
   */
  clear(editor: monacoEditor.IStandaloneCodeEditor) {
    this.decorations = editor.deltaDecorations(this.decorations, []);
  }
}
